import type { PostHogFeatureFlags } from '@lib/posthog-feature-flags';

export type MockFlagOverrides = Partial<Record<PostHogFeatureFlags, boolean>>;

declare global {
  interface Window {
    /** Set by e2e specs before the app mounts (see e2e/support/flags.ts). */
    __posthogFlagOverrides?: MockFlagOverrides;
  }
}

const MOCK_FLAGS_ON_SEPARATOR = ',';

/**
 * A flag's value in a mocked-backend run (`npm run e2e`, `npm run dev:mock:*`),
 * or undefined to fall through to PostHog. PostHog is not configured in those
 * runs, so without this every flag would sit at its hook default and a page
 * behind a launch flag could never be seen or tested.
 *
 * e2e specs set values per test on the window. `dev:mock:*` scripts list the
 * flag keys to switch on in NEXT_PUBLIC_MOCK_FLAGS_ON, comma separated.
 *
 * Deployed builds never set NEXT_PUBLIC_USE_MOCK_FFH_BACKEND, so there this
 * returns undefined before reading either source.
 */
export const readMockFlagOverride = (
  flag: PostHogFeatureFlags
): boolean | undefined => {
  if (process.env.NEXT_PUBLIC_USE_MOCK_FFH_BACKEND !== 'true') return undefined;

  const pageOverride =
    typeof window === 'undefined'
      ? undefined
      : window.__posthogFlagOverrides?.[flag];
  if (pageOverride !== undefined) return pageOverride;

  const flagsOn =
    process.env.NEXT_PUBLIC_MOCK_FLAGS_ON?.split(MOCK_FLAGS_ON_SEPARATOR) ?? [];
  return flagsOn.includes(flag) ? true : undefined;
};
